import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { invoiceSettingsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Save, Plus, Trash2, Bell, DollarSign, FileText } from 'lucide-react'
import type { UpdateInvoiceSettingsRequest, ReminderScheduleItem } from '@/types'

export default function InvoiceSettingsPage() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { data: settings, isLoading } = useQuery({
    queryKey: ['invoice-settings'],
    queryFn: invoiceSettingsApi.get,
  })

  const [formData, setFormData] = useState<UpdateInvoiceSettingsRequest>({
    invoice_number_prefix: 'INV',
    invoice_number_suffix: '',
    next_invoice_number: 1,
    invoice_number_padding: 5,
    include_year_in_number: true,
    year_format: 'YYYY',
    default_currency: 'USD',
    default_tax_rate: 0,
    default_payment_terms: 'Net 30',
    default_notes: '',
    default_due_days: 30,
    enable_payment_reminders: false,
    reminder_schedule: [],
    enable_late_fees: false,
    late_fee_type: 'PERCENTAGE',
    late_fee_value: 0,
    late_fee_grace_days: 0,
  })

  useEffect(() => {
    if (settings) {
      setFormData({
        invoice_number_prefix: settings.invoice_number_prefix,
        invoice_number_suffix: settings.invoice_number_suffix,
        next_invoice_number: settings.next_invoice_number,
        invoice_number_padding: settings.invoice_number_padding,
        include_year_in_number: settings.include_year_in_number,
        year_format: settings.year_format,
        default_currency: settings.default_currency,
        default_tax_rate: settings.default_tax_rate,
        default_payment_terms: settings.default_payment_terms || '',
        default_notes: settings.default_notes || '',
        default_due_days: settings.default_due_days,
        enable_payment_reminders: settings.enable_payment_reminders,
        reminder_schedule: settings.reminder_schedule || [],
        enable_late_fees: settings.enable_late_fees,
        late_fee_type: settings.late_fee_type || 'PERCENTAGE',
        late_fee_value: settings.late_fee_value,
        late_fee_grace_days: settings.late_fee_grace_days,
      })
    }
  }, [settings])

  const updateMutation = useMutation({
    mutationFn: invoiceSettingsApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-settings'] })
      toast.success(t('invoiceSettings.settingsSaved'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('invoiceSettings.failedToSave')
      toast.error(errorMessage)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateMutation.mutate(formData)
  }

  const generatePreviewNumber = () => {
    let preview = ''
    if (formData.invoice_number_prefix) {
      preview += formData.invoice_number_prefix + '-'
    }
    if (formData.include_year_in_number) {
      const year = formData.year_format === 'YY' 
        ? new Date().getFullYear().toString().slice(-2)
        : new Date().getFullYear().toString()
      preview += year + '-'
    }
    const num = formData.next_invoice_number || 1
    const padding = formData.invoice_number_padding || 5
    preview += num.toString().padStart(padding, '0')
    if (formData.invoice_number_suffix) {
      preview += '-' + formData.invoice_number_suffix
    }
    return preview
  }

  const addReminder = (type: 'before' | 'after') => {
    const newReminder: ReminderScheduleItem = type === 'before' 
      ? { days_before_due: 3 }
      : { days_after_due: 1 }
    setFormData(prev => ({
      ...prev,
      reminder_schedule: [...(prev.reminder_schedule || []), newReminder],
    }))
  }

  const removeReminder = (index: number) => {
    setFormData(prev => ({
      ...prev,
      reminder_schedule: prev.reminder_schedule?.filter((_, i) => i !== index) || [],
    }))
  }

  const updateReminder = (index: number, value: number, type: 'before' | 'after') => {
    setFormData(prev => ({
      ...prev,
      reminder_schedule: prev.reminder_schedule?.map((item, i) => {
        if (i !== index) return item
        return type === 'before' 
          ? { days_before_due: value }
          : { days_after_due: value }
      }) || [],
    }))
  }

  if (isLoading) {
    return <div className="p-6">{t('common.loading')}</div>
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('invoiceSettings.title')}</h1>
        <p className="text-muted-foreground">{t('invoiceSettings.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice Number Format */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t('invoiceSettings.invoiceNumberFormat')}
            </CardTitle>
            <CardDescription>{t('invoiceSettings.configureNumberGeneration')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('invoiceSettings.prefix')}</Label>
                <Input
                  value={formData.invoice_number_prefix || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoice_number_prefix: e.target.value }))}
                  placeholder="INV"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('invoiceSettings.suffix')}</Label>
                <Input
                  value={formData.invoice_number_suffix || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoice_number_suffix: e.target.value }))}
                  placeholder={t('common.optional')}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>{t('invoiceSettings.nextInvoiceNumber')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.next_invoice_number || 1}
                  onChange={(e) => setFormData(prev => ({ ...prev, next_invoice_number: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('invoiceSettings.numberPadding')}</Label>
                <Select
                  value={String(formData.invoice_number_padding || 5)}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, invoice_number_padding: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">{t('invoiceSettings.threeDigits')}</SelectItem>
                    <SelectItem value="4">{t('invoiceSettings.fourDigits')}</SelectItem>
                    <SelectItem value="5">{t('invoiceSettings.fiveDigits')}</SelectItem>
                    <SelectItem value="6">{t('invoiceSettings.sixDigits')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('invoiceSettings.yearFormat')}</Label>
                <Select
                  value={formData.year_format || 'YYYY'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, year_format: value }))}
                  disabled={!formData.include_year_in_number}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="YYYY">{t('invoiceSettings.fourDigitYear')}</SelectItem>
                    <SelectItem value="YY">{t('invoiceSettings.twoDigitYear')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.include_year_in_number}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_year_in_number: checked }))}
              />
              <Label>{t('invoiceSettings.includeYearInNumber')}</Label>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <Label className="text-sm text-muted-foreground">{t('invoiceSettings.preview')}</Label>
              <p className="text-lg font-mono mt-1">{generatePreviewNumber()}</p>
            </div>
          </CardContent>
        </Card>

        {/* Default Values */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {t('invoiceSettings.defaultValues')}
            </CardTitle>
            <CardDescription>{t('invoiceSettings.setDefaultValues')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>{t('invoiceSettings.defaultCurrency')}</Label>
                <Select
                  value={formData.default_currency || 'USD'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, default_currency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                    <SelectItem value="GBP">GBP (£)</SelectItem>
                    <SelectItem value="INR">INR (₹)</SelectItem>
                    <SelectItem value="CAD">CAD ($)</SelectItem>
                    <SelectItem value="AUD">AUD ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('invoiceSettings.defaultTaxRate')}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.default_tax_rate || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_tax_rate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('invoiceSettings.defaultDueDays')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.default_due_days || 30}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_due_days: parseInt(e.target.value) || 30 }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('invoiceSettings.defaultPaymentTerms')}</Label>
              <Textarea
                value={formData.default_payment_terms || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, default_payment_terms: e.target.value }))}
                placeholder={t('invoiceSettings.paymentTermsPlaceholder')}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('invoiceSettings.defaultNotes')}</Label>
              <Textarea
                value={formData.default_notes || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, default_notes: e.target.value }))}
                placeholder={t('invoiceSettings.notesPlaceholder')}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Payment Reminders */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t('invoiceSettings.paymentReminders')}
            </CardTitle>
            <CardDescription>{t('invoiceSettings.configureReminders')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.enable_payment_reminders}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_payment_reminders: checked }))}
              />
              <Label>{t('invoiceSettings.enablePaymentReminders')}</Label>
            </div>

            {formData.enable_payment_reminders && (
              <>
                <Separator />
                <div className="space-y-3">
                  {formData.reminder_schedule?.map((reminder, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground w-24">
                        {reminder.days_before_due !== undefined ? t('invoiceSettings.beforeDue') : t('invoiceSettings.afterDue')}
                      </span>
                      <Input
                        type="number"
                        min="1"
                        className="w-20"
                        value={reminder.days_before_due ?? reminder.days_after_due ?? 1}
                        onChange={(e) => updateReminder(
                          index,
                          parseInt(e.target.value) || 1,
                          reminder.days_before_due !== undefined ? 'before' : 'after'
                        )}
                      />
                      <span className="text-sm text-muted-foreground">{t('invoiceSettings.days')}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeReminder(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => addReminder('before')}>
                    <Plus className="h-4 w-4 mr-1" /> {t('invoiceSettings.beforeDueDate')}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => addReminder('after')}>
                    <Plus className="h-4 w-4 mr-1" /> {t('invoiceSettings.afterDueDate')}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={updateMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? t('common.saving') : t('invoiceSettings.saveSettings')}
          </Button>
        </div>
      </form>
    </div>
  )
}

